import { useMemo, useState } from "react";
import { Search, MapPin } from "lucide-react";
import DestinationSpotlight from "@/components/DestinationSpotlight";

// Daily-rotating hero pool. One photo per day; click-to-spotlight.
// All Unsplash URLs use `&fm=webp` for ~30% smaller mobile downloads.
const HERO_POOL = [
  { label: "Maldives",   alt: "Maldives luxury honeymoon water villa packages from Ludhiana", url: "https://images.pexels.com/photos/1287460/pexels-photo-1287460.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=2000" },
  { label: "Bali",       alt: "Bali honeymoon packages from Ludhiana — Vacations Pro",        url: "https://images.unsplash.com/photo-1537996194471-e657df975ab4?w=2000&q=80&fm=webp" },
  { label: "Kashmir",    alt: "Kashmir Srinagar Gulmarg luxury houseboat tours",              url: "https://images.unsplash.com/photo-1605649487212-47bdab064df7?w=2000&q=80&fm=webp" },
  { label: "Dubai",      alt: "Dubai family vacation packages from Punjab",                   url: "https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=2000&q=80&fm=webp" },
  { label: "Singapore",  alt: "Singapore Malaysia cruise deals for families",                 url: "https://images.unsplash.com/photo-1525625293386-3f8f99389edd?w=2000&q=80&fm=webp" },
  { label: "Goa",        alt: "Goa beach holiday packages — best travel agency in Ludhiana",  url: "https://images.unsplash.com/photo-1512343879784-a960bf40e7f2?w=2000&q=80&fm=webp" },
  { label: "Thailand",   alt: "Thailand Phuket Krabi family tours from Ludhiana",             url: "https://images.unsplash.com/photo-1508009603885-50cf7c579365?w=2000&q=80&fm=webp" },
  { label: "Santorini",  alt: "Santorini Greece luxury honeymoon packages",                   url: "https://images.unsplash.com/photo-1533105079780-92b9be482077?w=2000&q=80&fm=webp" },
  { label: "Swiss Alps", alt: "Swiss Rail Pass Ludhiana — Luxury Swiss Alps family tours",    url: "https://images.unsplash.com/photo-1531366936337-7c912a4589a7?w=2000&q=80&fm=webp" },
  { label: "Istanbul",   alt: "Istanbul Turkey heritage tour packages",                       url: "https://images.unsplash.com/photo-1527838832700-5059252407fa?w=2000&q=80&fm=webp" },
  { label: "Paris",      alt: "Paris to Zurich scenic train packages — Vacations Pro",        url: "https://images.pexels.com/photos/161901/paris-sunset-france-monument-161901.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=2000" },
  { label: "Ladakh",     alt: "Leh Ladakh luxury 4x4 tours 2026 from Ludhiana",               url: "https://images.unsplash.com/photo-1519451241324-20b4ea2c4220?w=2000&q=80&fm=webp" },
  { label: "Tokyo",      alt: "Japan Cherry Blossom tours 2026 from India",                   url: "https://images.unsplash.com/photo-1540959733332-eab4deabeeaf?w=2000&q=80&fm=webp" },
  { label: "New Zealand",alt: "New Zealand luxury family vacation packages",                  url: "https://images.unsplash.com/photo-1469521669194-babb45599def?w=2000&q=80&fm=webp" },
];

const MOODS = [
  { key: "Honeymoon", label: "Honeymoon", destination: "a romantic honeymoon escape" },
  { key: "Family", label: "Family", destination: "a family-friendly getaway" },
  { key: "Adventure", label: "Adventure", destination: "an adventure trek" },
  { key: "Culture", label: "Culture", destination: "a cultural heritage tour" },
];

const SUGGESTIONS = ["Goa", "Kashmir", "Bali", "Singapore", "Maldives", "Dubai", "Thailand"];

export default function Hero({ onSearch }) {
  const [value, setValue] = useState("");
  const [spotlightOpen, setSpotlightOpen] = useState(false);
  const [spotlightDest, setSpotlightDest] = useState("");

  // Daily rotation — deterministic, new photo every 24h
  const featured = useMemo(() => {
    const dayIndex = Math.floor(Date.now() / 86400000);
    return HERO_POOL[dayIndex % HERO_POOL.length];
  }, []);

  // All destination-style triggers (chips, hero photo, Journey of the Day) funnel
  // into the spotlight modal instead of opening chat directly.
  const openSpotlight = (dest) => {
    setSpotlightDest(dest);
    setSpotlightOpen(true);
  };

  // Called from the Spotlight CTA — hands off to the existing chat flow.
  const planFromSpotlight = (dest) => onSearch(dest);

  const submit = (e) => {
    e.preventDefault();
    const v = value.trim();
    if (!v) return;
    openSpotlight(v);
  };

  return (
    <section data-testid="hero-section" className="relative w-full bg-white">
      {/* Full-width daily photo — clickable background */}
      <div
        className="relative w-full h-[72vh] md:h-[78vh] overflow-hidden"
        data-testid="hero-photo"
      >
        <button
          type="button"
          onClick={() => openSpotlight(featured.label)}
          className="absolute inset-0 block w-full h-full focus:outline-none"
          aria-label={`Open spotlight: ${featured.label}`}
        >
          <img
            src={featured.url}
            alt={featured.alt || featured.label}
            onError={(e) => {
              // If a remote image is removed (Unsplash 404s), gracefully fall back
              // to a known-stable Pexels photo so the hero never shows a broken icon.
              if (!e.currentTarget.dataset.fallback) {
                e.currentTarget.dataset.fallback = "1";
                e.currentTarget.src =
                  "https://images.pexels.com/photos/1287460/pexels-photo-1287460.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=2000";
              }
            }}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[8000ms] hover:scale-[1.02]"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/15 to-black/75" />
        </button>

        {/* Centered tagline — tighter to the search bar below */}
        <div className="relative z-10 h-full flex flex-col items-center text-center px-6 pt-[14vh] md:pt-[16vh] pointer-events-none">
          <h1 className="text-white font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] drop-shadow-[0_2px_24px_rgba(0,0,0,0.45)]">
            Customized Honeymoon & Family Holidays in India & Abroad
          </h1>
          <h2 className="text-[#ffe9a8] font-medium text-2xl sm:text-3xl lg:text-4xl mt-3 tracking-tight drop-shadow-[0_2px_16px_rgba(0,0,0,0.5)]">
            Planned by Experience.
          </h2>
        </div>

        {/* Search + chips + mood overlay — anchored near middle of the hero photo to sit right under the tagline */}
        <div
          className="absolute inset-x-0 top-[44%] md:top-[46%] z-20 px-4 pointer-events-none"
          data-testid="hero-search-overlay"
        >
          <div className="max-w-[760px] mx-auto pointer-events-auto">
            <form onSubmit={submit} data-testid="destination-search-form">
              <div
                className="group flex items-center bg-white/95 backdrop-blur rounded-full border border-white/60
                           shadow-[0_14px_40px_rgba(0,0,0,0.35)]
                           hover:shadow-[0_18px_46px_rgba(0,0,0,0.45)]
                           focus-within:shadow-[0_22px_54px_rgba(26,115,232,0.32)]
                           focus-within:border-[#1a73e8]
                           transition-all overflow-hidden pl-5 pr-2 py-2"
              >
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <MapPin className="w-5 h-5 text-[var(--vp-secondary)] shrink-0" strokeWidth={2} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-[var(--vp-muted)]">
                      Where to?
                    </div>
                    <input
                      data-testid="destination-search-input"
                      type="text"
                      placeholder="Type a destination — Bali, Maldives, Dubai…"
                      value={value}
                      onChange={(e) => setValue(e.target.value)}
                      className="w-full bg-transparent border-0 outline-none text-[15px] text-[var(--vp-text)] placeholder:text-[var(--vp-muted)] py-0.5"
                    />
                  </div>
                </div>
                <button
                  data-testid="search-submit-btn"
                  type="submit"
                  className="shrink-0 bg-[#1a73e8] hover:bg-[#1664c1] active:bg-[#1558ad] text-white rounded-full px-5 py-3 text-sm font-semibold transition-colors flex items-center gap-2 shadow-sm"
                >
                  <Search className="w-4 h-4" strokeWidth={2.25} />
                  <span className="hidden sm:inline">Explore</span>
                </button>
              </div>

              {/* Popular chips */}
              <div className="flex items-center justify-center gap-2 flex-wrap mt-4" data-testid="quick-suggestions">
                <span className="text-xs text-white/85 mr-1 tracking-wide uppercase">Popular:</span>
                {SUGGESTIONS.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => openSpotlight(s)}
                    className="text-xs px-3 py-1.5 rounded-full border border-white/50 bg-white/15 backdrop-blur text-white hover:bg-white hover:text-[var(--vp-primary)] transition-colors"
                    data-testid={`suggestion-${s.toLowerCase()}`}
                  >
                    {s}
                  </button>
                ))}
              </div>

              {/* Mood pills — moved into the overlay, sits right under Popular chips */}
              <div className="flex items-center justify-center gap-2 flex-wrap mt-3 text-sm" data-testid="mood-pills">
                <span className="text-white/85 text-xs tracking-wide uppercase">Or by mood:</span>
                {MOODS.map((m, i) => (
                  <span key={m.key} className="text-xs">
                    <button
                      type="button"
                      onClick={() => onSearch(m.destination, m.key)}
                      className="text-[#ffe9a8] hover:text-white hover:underline font-semibold"
                      data-testid={`mood-pill-${m.key.toLowerCase()}`}
                    >
                      {m.label}
                    </button>
                    {i < MOODS.length - 1 && <span className="text-white/60 mx-1.5">·</span>}
                  </span>
                ))}
              </div>
            </form>
          </div>
        </div>

        {/* Journey-of-the-day label — click opens spotlight */}
        <button
          type="button"
          onClick={() => openSpotlight(featured.label)}
          className="absolute bottom-3 right-4 z-20 text-[11px] text-white/85 tracking-[0.28em] uppercase font-medium drop-shadow-[0_1px_6px_rgba(0,0,0,0.6)] hover:text-[var(--vp-accent)] transition-colors"
          data-testid="hero-location-label"
        >
          {featured.label}
        </button>
      </div>

      <DestinationSpotlight
        open={spotlightOpen}
        onOpenChange={setSpotlightOpen}
        destination={spotlightDest}
        onPlan={planFromSpotlight}
      />
    </section>
  );
}
